import { useEffect, useMemo, useRef, useState } from 'react';
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import MapLegendOverlay from './MapLegendOverlay';
import WorkplaceMarker from './marker/WorkplaceMarker';
import { useTranslation } from 'react-i18next';
import LocationMarker from './marker/LocationMarker';
import { User, useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { MapIconType } from './pin/MapPin';
import {
  getWorkplaceMarkerZIndex,
  getDetailStatusTranslationKey,
} from 'src/api/wa/store/models/workplace/OperationStatus';
import { Workplace } from 'src/api/wa/store/models/workplace';
import {
  defaultMapCenter,
  defaultMapSearchRange,
  defaultMapZoom,
} from 'src/common/location/defaultMapSettings';
import useWaUsersService from 'src/api/wa/service/WaUsersService';
import { TrafficLayer } from './trafficLayer/TrafficLayer';
import { InfoWindow } from '@vis.gl/react-google-maps';
import LinkButton from '../button/LinkButton';
import Button from '../button/Button';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { useNavigate } from 'react-router-dom';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { useNotifyWithParam } from 'src/ui/common/alert/useNotifyWithParam';

export type PopupChangedEvent = { userId?: string; customerId?: string };

export type MapProps = {
  workers: User[];
  user: User | null;
  workplaces: Workplace[];
  className?: string;
  legendButtonVisible?: boolean;
  popupOpened?: (event: PopupChangedEvent) => void;
  popupClosed?: (event: PopupChangedEvent) => void;
  selectedWorkerId?: string | null;
  onWorkerSelected?: (userId: string | null) => void;
  center: { latitude: number; longitude: number } | null;
  showTraffic?: boolean;
  recenterTrigger?: number;
};

const MapCenterUpdater = ({
  center,
  recenterTrigger,
}: {
  center: { lat: number; lng: number };
  recenterTrigger: number | undefined;
}) => {
  const map = useMap();
  const prevCenterRef = useRef<{ lat: number; lng: number } | null>(null);
  const prevTriggerRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (map && center) {
      const prev = prevCenterRef.current;
      const triggerChanged = recenterTrigger !== prevTriggerRef.current;
      if (triggerChanged || !prev || prev.lat !== center.lat || prev.lng !== center.lng) {
        map.panTo(center);
        prevCenterRef.current = { lat: center.lat, lng: center.lng };
        prevTriggerRef.current = recenterTrigger;
      }
    }
  }, [map, center, recenterTrigger]);
  return null;
};

const WaMap = ({
  workers,
  workplaces,
  user,
  className,
  legendButtonVisible,
  popupOpened,
  popupClosed,
  selectedWorkerId,
  onWorkerSelected,
  showTraffic = false,
  recenterTrigger,
}: MapProps) => {
  const {
    workplaceFilterParameters,
    salesOfficeOptions,
    sectionOptions,
    mapCenter,
    userOfficeLocation,
  } = useWaFilterStore();
  const { t } = useTranslation();
  const { getUsers } = useWaUsersService();
  const [showOverlay, setShowOverlay] = useState(false);
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);
  const { setAvailableWorkers: setAvailableUsers, user: currentUser } = useWaUserStore();
  const userAgent = useUserAgent();
  const { createWorkplaceReservation } = useWaWorkplaceService();
  const notify = useNotify();
  const notifyWithParam = useNotifyWithParam();

  const selectedSalesOffice = useMemo(() => {
    const salesOfficeCode = workplaceFilterParameters.salesOfficeCode
      ? workplaceFilterParameters.salesOfficeCode
      : undefined;
    return salesOfficeOptions.find((office) => office.code === salesOfficeCode);
  }, [salesOfficeOptions, workplaceFilterParameters.salesOfficeCode]);

  const selectedSection = useMemo(() => {
    const sectionCode = workplaceFilterParameters.sectionCode
      ? workplaceFilterParameters.sectionCode
      : undefined;
    return sectionOptions.find((section) => section.code === sectionCode);
  }, [sectionOptions, workplaceFilterParameters.sectionCode]);

  const selectedSalesOfficeScale = selectedSalesOffice?.defaultScale;
  const selectedSectionScale = selectedSection?.defaultScale;
  const selectedSalesOfficeSearchRange = selectedSalesOffice?.searchRange;
  const selectedSectionSearchRange = selectedSection?.searchRange;

  const { setSelectedWorkplace } = useWaWorkplaceStore();
  const navigate = useNavigate();

  const markers = useMemo(() => {
    if (!workplaces) return [];

    return workplaces
      .filter((site) => site.location != null)
      .map((site) => (
        <AdvancedMarker
          key={site.customerId}
          clickable
          position={{
            lat: site.location.latitude,
            lng: site.location.longitude,
          }}
          zIndex={getWorkplaceMarkerZIndex(site.details?.statusId)}
          onClick={() => {
            popupOpened?.({ customerId: site.customerId });
            setActiveCustomerId(site.customerId);
          }}
        >
          <WorkplaceMarker site={site} canOpenPopup={false} />
        </AdvancedMarker>
      ));
  }, [workplaces, popupOpened]);

  const searchRange =
    selectedSectionSearchRange ?? selectedSalesOfficeSearchRange ?? defaultMapSearchRange;

  const mapZoom = useMemo(() => {
    const isCustomerSearch = Boolean(workplaceFilterParameters.customerId?.trim());

    if (!isCustomerSearch && Number.isFinite(selectedSectionScale)) {
      return selectedSectionScale as number;
    }

    if (!isCustomerSearch && Number.isFinite(selectedSalesOfficeScale)) {
      return selectedSalesOfficeScale as number;
    }

    return defaultMapZoom;
  }, [selectedSectionScale, selectedSalesOfficeScale, workplaceFilterParameters.customerId]);

  useEffect(() => {
    if (!userOfficeLocation || !userOfficeLocation.latitude || !userOfficeLocation.longitude) {
      setAvailableUsers([]);
      return;
    }

    getUsers(userOfficeLocation.latitude, userOfficeLocation.longitude, searchRange);
  }, [getUsers, searchRange, userOfficeLocation, setAvailableUsers]);

  ////////////////

  const activeSite = useMemo(() => {
    return workplaces.find((site) => site.customerId === activeCustomerId);
  }, [workplaces, activeCustomerId]);

  const isActiveSiteReserved = (activeSite?.details?.reservations?.length ?? 0) > 0;

  const handleReserveActiveSite = async () => {
    setActiveCustomerId(null);
    if (!currentUser || !activeSite) return;
    const reservationParams = [
      {
        engineerUserId: currentUser.id,
        branchCode: activeSite.branchCode,
        customerId: activeSite.customerId,
      },
    ];
    try {
      const response = await createWorkplaceReservation(reservationParams);
      if (response) {
        notify('wards.sites.reservationSuccess');
      } else {
        notifyWithParam('wards.sites.reservationFailed', 'workplaces', activeSite.customerId);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  /////////////////
  const currentCenter = useMemo(() => {
    /**
     * Here handles following cases:
     *
     * Mobile Map Centering:
     * 1. Searched with Organization, Selected "Centering on Sales Office" -> Center on Sales Office (or Section)
     * 2. Searched with Organization, Selected "Centering on User Location" -> Center on User Location
     * 3. Searched with Customer -> Center on Customer Location
     * 4. If in above cases, if the location data is not available, center on default location
     *
     * PC Map Centering:
     * 1. Searched with Customer -> Center on Customer Location
     * 2. If in above cases, if the location data is not available, center on default location
     */
    if (workplaceFilterParameters.customerId) {
      const customerLocation = workplaces.find(
        (workplace) => workplace.customerId === workplaceFilterParameters.customerId
      )?.location;
      if (customerLocation) {
        return { lat: customerLocation.latitude, lng: customerLocation.longitude };
      } else {
        return { lat: defaultMapCenter.latitude, lng: defaultMapCenter.longitude };
      }
    }

    if (mapCenter) {
      return { lat: mapCenter.latitude, lng: mapCenter.longitude };
    }
    return { lat: defaultMapCenter.latitude, lng: defaultMapCenter.longitude };
  }, [mapCenter, workplaceFilterParameters.customerId, workplaces]);

  return (
    <div data-testid="map-wrapper" className={`relative size-full ${className ?? ''}`}>
      {Number.isFinite(mapZoom) && (
        <Map
          defaultCenter={currentCenter}
          defaultZoom={mapZoom}
          mapId="WA_MAIN_MAP"
          className="size-full"
          disableDefaultUI={false}
          fullscreenControl={true}
          gestureHandling={'greedy'}
        >
          <MapCenterUpdater center={currentCenter} recenterTrigger={recenterTrigger} />
          <TrafficLayer visible={showTraffic} />

          {markers}

          {activeSite && (
            <InfoWindow
              position={{
                lat: activeSite.location.latitude,
                lng: activeSite.location.longitude,
              }}
              onCloseClick={() => {
                setActiveCustomerId(null);
                popupClosed?.({ customerId: activeSite.customerId });
              }}
            >
              <div className="flex flex-col gap-2 p-1 text-sm text-black">
                <LinkButton
                  title={activeSite.customerId}
                  onClick={() => {
                    setSelectedWorkplace(activeSite);
                    wardsNavigate(navigate, WaScreensMap[WA_Screen.WorkplaceDetails].path);
                  }}
                />
                <label className="m-0 text-center font-medium">
                  {activeSite.emergencyClass ? '★' : ''}
                  {activeSite.name}
                </label>
                <label className="m-0 text-center font-bold">
                  {t(getDetailStatusTranslationKey(activeSite?.details?.statusId))}
                </label>
                <label className="m-0 text-center font-bold">
                  {activeSite?.details?.comment
                    ? t('wards.common.withComment')
                    : t('wards.common.noComment')}
                </label>
                {!isActiveSiteReserved && !userAgent.isDesktop && (
                  <Button
                    className="rounded border border-blue-600 px-3 py-1 text-blue-600 transition hover:bg-blue-600 hover:text-white"
                    title={t('wards.common.reserve')}
                    onClick={handleReserveActiveSite}
                  />
                )}
                {userAgent.isDesktop && (
                  <Button
                    className="rounded border border-blue-600 px-3 py-1 text-blue-600 transition hover:bg-blue-600 hover:text-white"
                    title={t('wards.common.select')}
                    onClick={() => setSelectedWorkplace(activeSite)}
                  />
                )}
              </div>
            </InfoWindow>
          )}

          {user?.location && (
            <AdvancedMarker
              position={{ lat: user.location.latitude, lng: user.location.longitude }}
              clickable
              onClick={() => popupOpened?.({ userId: user.id })}
            >
              <LocationMarker
                type={
                  user?.branchCode == workplaceFilterParameters.branchCode &&
                  user?.salesOfficeCode == workplaceFilterParameters.salesOfficeCode
                    ? MapIconType.currentUserMatch
                    : MapIconType.currentUserNoMatch
                }
                location={user.location}
                user={user}
                canOpenPopup={true}
                popupClosed={() => popupClosed?.({ userId: user.id })}
                onWorkerSelected={onWorkerSelected}
                selectedWorkerId={selectedWorkerId ?? null}
              />
            </AdvancedMarker>
          )}

          {workers
            .filter((worker) => worker.id !== user?.id)
            .map((worker) => (
              <AdvancedMarker
                key={worker.id}
                clickable
                position={{ lat: worker.location.latitude, lng: worker.location.longitude }}
                onClick={() => popupOpened?.({ userId: worker.id })}
              >
                <LocationMarker
                  type={
                    worker?.branchCode == workplaceFilterParameters.branchCode &&
                    worker?.salesOfficeCode == workplaceFilterParameters.salesOfficeCode
                      ? MapIconType.worker
                      : MapIconType.workerNoMatch
                  }
                  location={worker.location}
                  user={worker}
                  canOpenPopup={true}
                  popupClosed={() => popupClosed?.({ userId: worker.id })}
                  onWorkerSelected={onWorkerSelected}
                  selectedWorkerId={selectedWorkerId ?? null}
                />
              </AdvancedMarker>
            ))}

          {userOfficeLocation && (
            <AdvancedMarker
              position={{ lat: userOfficeLocation.latitude, lng: userOfficeLocation.longitude }}
              clickable
            >
              <LocationMarker
                type={MapIconType.office}
                location={{
                  latitude: userOfficeLocation.latitude,
                  longitude: userOfficeLocation.longitude,
                }}
                canOpenPopup={false}
              />
            </AdvancedMarker>
          )}
        </Map>
      )}

      {legendButtonVisible && (
        <div className="absolute right-[58px] top-[10px] z-[1000]">
          <button
            data-testid="map-info-button"
            className="flex items-center justify-center rounded-[2px] bg-white p-2.5 text-[#666] shadow-md transition-colors hover:bg-gray-100"
            style={{ boxShadow: '0 1px 4px -1px rgba(0,0,0,0.3)' }}
            onClick={() => setShowOverlay(true)}
          >
            <InformationCircleIcon className="size-5" />
          </button>
        </div>
      )}

      {showOverlay && <MapLegendOverlay onClose={() => setShowOverlay(false)} />}
    </div>
  );
};

WaMap.displayName = 'WaMap';
export default WaMap;
